// Payees + worker-misclassification assessment. Shows each non-employee payee
// with a classification badge, and a questionnaire that flags whether they
// actually look like an employee (an advisory guardrail — a human confirms the
// final call, and a confirmed "likely employee" blocks non-employee payout).
// A payee who is ALSO an active employee of the same company is flagged and
// blocked automatically until a clearance is approved (EmployeeOverlapDialog).
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ShieldAlert, ShieldCheck, HelpCircle, ClipboardCheck, Briefcase } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Label } from '@/features/shared/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/features/shared/components/ui/dialog';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  commissionApi, type CommissionPayee, type ClassificationFactor, type ClassificationResult, type PayeeClassification,
  type EmployeeOverlap,
} from '../api/commissionApi';
import { EmployeeOverlapDialog } from './EmployeeOverlapDialog';

// 'error' = the check couldn't run: never shown as "no overlap".
// 'no_identity' = nothing to compare (no person link, TIN or email): payment is
// held until one is added.
type OverlapState = EmployeeOverlap | 'error' | 'no_identity';

const OUTCOME: Record<string, { label: string; variant: 'success' | 'warning' | 'danger' | 'secondary'; icon: React.ReactNode }> = {
  likely_independent: { label: 'Independent', variant: 'success', icon: <ShieldCheck className="h-3.5 w-3.5" /> },
  likely_employee: { label: 'Employee risk', variant: 'danger', icon: <ShieldAlert className="h-3.5 w-3.5" /> },
  requires_review: { label: 'Needs review', variant: 'warning', icon: <HelpCircle className="h-3.5 w-3.5" /> },
};

export function PayeesPanel({ payees }: { payees: CommissionPayee[] }) {
  const [status, setStatus] = useState<Record<string, string>>({});
  const [assessing, setAssessing] = useState<CommissionPayee | null>(null);
  const [overlaps, setOverlaps] = useState<Record<string, OverlapState>>({});
  const [reviewing, setReviewing] = useState<CommissionPayee | null>(null);

  const loadOverlap = async (id: string): Promise<OverlapState> => {
    try {
      return await commissionApi.getEmployeeOverlap(id);
    } catch (e: unknown) {
      return (e as any)?.response?.data?.code === 'overlap_identity_missing' ? 'no_identity' : 'error';
    }
  };

  useEffect(() => {
    let alive = true;
    Promise.all(payees.map((p) => loadOverlap(p.payee_id).then((o) => [p.payee_id, o] as const)))
      .then((pairs) => { if (alive) setOverlaps(Object.fromEntries(pairs)); });
    return () => { alive = false; };
  }, [payees]);

  const refreshOverlap = async (id: string) => {
    const o = await loadOverlap(id);
    setOverlaps((m) => ({ ...m, [id]: o }));
  };

  useEffect(() => {
    let alive = true;
    Promise.all(payees.map((p) => commissionApi.getClassification(p.payee_id).then((c) => [p.payee_id, c] as const)))
      .then((pairs) => {
        if (!alive) return;
        const m: Record<string, string> = {};
        pairs.forEach(([id, c]) => { if (c?.confirmed_outcome) m[id] = c.confirmed_outcome; else if (c?.suggested_outcome) m[id] = c.suggested_outcome + '_unconfirmed'; });
        setStatus(m);
      });
    return () => { alive = false; };
  }, [payees]);

  if (payees.length === 0) return null;

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Payees</h2>
            <p className="text-xs text-muted-foreground">Non-employees you pay. Run a classification check before treating someone as a contractor.</p>
          </div>
        </div>
        <div className="divide-y divide-border">
          {payees.map((p) => {
            const raw = status[p.payee_id];
            const confirmed = raw && !raw.endsWith('_unconfirmed');
            const code = raw ? raw.replace('_unconfirmed', '') : '';
            const o = code ? OUTCOME[code] : null;
            return (
              <div key={p.payee_id} className="flex items-center justify-between gap-4 px-5 py-3">
                <div>
                  <div className="font-medium text-foreground">{p.legal_name}</div>
                  <div className="text-xs text-muted-foreground">{p.worker_category.replace(/_/g, ' ').toLowerCase()} · {p.country_code}</div>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <OverlapBadge state={overlaps[p.payee_id]} onReview={() => setReviewing(p)} />
                  {o ? (
                    <Badge variant={o.variant} className="gap-1">{o.icon}{o.label}{!confirmed && ' (draft)'}</Badge>
                  ) : (
                    <Badge variant="secondary">Not assessed</Badge>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setAssessing(p)}>
                    <ClipboardCheck className="mr-1 h-4 w-4" /> Assess
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
      {reviewing && typeof overlaps[reviewing.payee_id] === 'object' && (
        <EmployeeOverlapDialog
          payee={reviewing}
          overlap={overlaps[reviewing.payee_id] as EmployeeOverlap}
          onClose={() => setReviewing(null)}
          onChanged={() => { void refreshOverlap(reviewing.payee_id); }}
        />
      )}
      {assessing && (
        <AssessmentDialog
          payee={assessing}
          onClose={() => setAssessing(null)}
          onDone={(outcome, confirmedNow) => {
            setStatus((s) => ({ ...s, [assessing.payee_id]: confirmedNow ? outcome : outcome + '_unconfirmed' }));
            setAssessing(null);
          }}
        />
      )}
    </Card>
  );
}

function OverlapBadge({ state, onReview }: { state: OverlapState | undefined; onReview: () => void }) {
  if (state === undefined) return null;
  if (state === 'error') {
    return <Badge variant="warning" title="Payment is held until the check succeeds">Couldn't check payroll</Badge>;
  }
  if (state === 'no_identity') {
    return (
      <Badge variant="warning" title="Payment is held until this person has a TIN, national ID or email to check against the employee list">
        Add a TIN or email to pay
      </Badge>
    );
  }
  if (state.matches.length === 0) return null;
  return (
    <Button size="sm" variant="outline" onClick={onReview}
      className={state.blocked ? 'border-danger text-danger' : 'border-success text-success'}>
      <Briefcase className="mr-1 h-4 w-4" aria-hidden="true" />
      {state.blocked ? (state.clearance?.status === 'pending' ? 'On payroll — awaiting approval' : 'On payroll — blocked') : 'On payroll — cleared'}
    </Button>
  );
}

function AssessmentDialog({ payee, onClose, onDone }: {
  payee: CommissionPayee; onClose: () => void; onDone: (outcome: string, confirmed: boolean) => void;
}) {
  const { canApprovePayroll } = useRoleAccess();
  const [factors, setFactors] = useState<ClassificationFactor[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ClassificationResult | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { commissionApi.getFactors().then(setFactors).catch(() => setFactors([])); }, []);

  const answered = Object.keys(answers).length;

  const runAssessment = async () => {
    if (answered < factors.length) { toast.error('Answer every factor first'); return; }
    setBusy(true);
    try {
      const r = await commissionApi.assess(payee.payee_id, answers);
      setResult(r);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Assessment failed'));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!result) return;
    if (!reason.trim()) { toast.error('A reason is required to confirm'); return; }
    setBusy(true);
    try {
      await commissionApi.confirmClassification(result.assessment_id, result.suggested_outcome, reason.trim());
      toast.success('Classification confirmed');
      onDone(result.suggested_outcome, true);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not confirm'));
    } finally {
      setBusy(false);
    }
  };

  const o = result ? OUTCOME[result.suggested_outcome] : null;

  return (
    <Dialog open onOpenChange={(x) => !x && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Classification check — {payee.legal_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          Answer each factor for how this working relationship actually operates. This is advisory — it flags misclassification risk; it does not make a legal determination.
        </p>
        <div className="space-y-3">
          {factors.map((f) => (
            <div key={f.key} className="rounded-md border border-border p-3">
              <div className="mb-2 text-sm font-medium text-foreground">{f.question}</div>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
                {[['employee', f.employee_label], ['independent', f.independent_label], ['unclear', 'Unclear']].map(([lean, lbl]) => (
                  <button
                    key={lean}
                    type="button"
                    onClick={() => setAnswers((a) => ({ ...a, [f.key]: lean }))}
                    className={`rounded-md border px-2.5 py-1.5 text-left text-xs transition ${
                      answers[f.key] === lean ? 'border-primary-500 bg-primary-500/10 font-medium text-foreground' : 'border-border text-muted-foreground hover:border-primary-500/40'
                    }`}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {result && o && (
          <div className={`rounded-md border p-3 ${result.suggested_outcome === 'likely_employee' ? 'border-danger bg-danger-soft' : result.suggested_outcome === 'likely_independent' ? 'border-success bg-success-soft' : 'border-warning bg-warning-soft'}`}>
            <div className="flex items-center gap-2 font-medium text-foreground">{o.icon} Suggested: {o.label}</div>
            <p className="mt-1 text-sm text-muted-foreground">{result.rationale}</p>
            {canApprovePayroll ? (
              <div className="mt-3 space-y-1.5">
                <Label className="text-xs">Confirm this classification (recorded with your name)</Label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason for the confirmed classification…"
                  className="w-full rounded-md border border-border bg-background p-2 text-sm"
                  rows={2}
                />
              </div>
            ) : (
              <p className="mt-2 text-xs text-muted-foreground">An approver must confirm the final classification.</p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {!result ? (
            <Button onClick={runAssessment} disabled={busy || answered < factors.length}>
              {busy ? 'Scoring…' : `Assess (${answered}/${factors.length})`}
            </Button>
          ) : canApprovePayroll ? (
            <Button onClick={confirm} disabled={busy}>Confirm classification</Button>
          ) : (
            <Button onClick={() => onDone(result.suggested_outcome, false)}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PayeesPanel;
